import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { KIDS_SPACE_PUBLIC, KIDS_SPACE_PREVIEW_EMAILS } from '../../config/kidsSpaceConfig';
import './Header.css';

// Theme Switcher Component
const ThemeSwitcher = () => {
  const { currentTheme, setTheme, themes } = useTheme();
  
  return (
    <div className="theme-switcher">
      <div className="theme-options">
        {Object.keys(themes).map(themeName => (
          <button
            key={themeName}
            className={`theme-option ${currentTheme === themeName ? 'active' : ''}`}
            style={{ backgroundColor: themes[themeName].primary }}
            onClick={() => setTheme(themeName)}
            aria-label={`Switch to ${themes[themeName].name} theme`}
            title={themes[themeName].name}
          />
        ))}
      </div>
    </div>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  const languages = [
    { code: 'en', name: 'EN', flag: '🇬🇧' },
    { code: 'sk', name: 'SK', flag: '🇸🇰' },
    { code: 'hu', name: 'HU', flag: '🇭🇺' }
  ];
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };
  
  return (
    <div className="language-switcher">
      <div className="language-options">
        {languages.map(language => (
          <button
            key={language.code}
            className={`language-option ${currentLanguage === language.code ? 'active' : ''}`}
            onClick={() => changeLanguage(language.code)}
            aria-label={`Switch to ${language.name} language`}
            title={language.name}
            lang={language.code}
            hrefLang={language.code}
          >
            <span className="language-flag">{language.flag}</span>
            <span className="language-name">{language.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Header Component
const Header = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const navigate = useNavigate();
  
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';
  
  // Preload logo image
  useEffect(() => {
    const logoImage = new Image();
    logoImage.src = '/images/backgrounds/header2.jpg';
    logoImage.onload = () => {
      setLogoLoaded(true);
    };
  }, []);
  
  // Add SEO metadata for Slovak language users
  useEffect(() => {
    if (isSlovak) {
      // Set language attribute on html tag
      document.documentElement.setAttribute('lang', 'sk');
      
      // Add hreflang alternate links
      const createOrUpdateLinkElement = (rel, hreflang, href) => {
        let linkElement = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
        
        if (!linkElement) {
          linkElement = document.createElement('link');
          linkElement.setAttribute('rel', rel);
          linkElement.setAttribute('hreflang', hreflang);
          document.head.appendChild(linkElement);
        }
        
        linkElement.setAttribute('href', href);
      };
      
      // Add hreflang links
      createOrUpdateLinkElement('alternate', 'sk', window.location.href);
      createOrUpdateLinkElement('alternate', 'en', window.location.href.replace('/sk/', '/en/'));
      createOrUpdateLinkElement('alternate', 'hu', window.location.href.replace('/sk/', '/hu/'));
      createOrUpdateLinkElement('alternate', 'x-default', window.location.href.replace('/sk/', '/en/'));
      
      // Add Open Graph locale meta tag for Slovak
      let ogLocale = document.querySelector('meta[property="og:locale"]');
      if (!ogLocale) {
        ogLocale = document.createElement('meta');
        ogLocale.setAttribute('property', 'og:locale');
        document.head.appendChild(ogLocale);
      }
      ogLocale.setAttribute('content', 'sk_SK');
    }
    
    // Add structured data
    const addJsonLdScript = (id, jsonData) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonData);
    };
    
    // Organization schema
    const websiteStructuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "TiDeaSolis Yoga Štúdio",
      "url": window.location.origin,
      "logo": `${window.location.origin}/images/logos/yoga-center-logo.png`,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+421 903 123 456",
        "contactType": "customer service",
        "availableLanguage": ["English", "Slovak", "Hungarian"]
      },
      "sameAs": [
        "https://www.facebook.com/search/top?q=tideasolis",
        "https://www.instagram.com/tideasolisyoga/?hl=cs"
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Šafárikova 37",
        "addressLocality": "Rožňava",
        "addressCountry": "SK"
      }
    };
    
    // Breadcrumb schema
    const breadcrumbStructuredData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": isSlovak ? "Domov" : "Home",
          "item": window.location.origin
        }
      ]
    };
    
    addJsonLdScript('organization-schema', websiteStructuredData);
    addJsonLdScript('breadcrumb-schema', breadcrumbStructuredData);
    
    return () => {
      // Cleanup function is optional here as these changes persist
      // across component lifecycle, but could be added if needed
    };
  }, [isSlovak]);
  
  // Handle scroll effect and header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolledDown = currentScrollY > lastScrollY;
      
      // Set scrolled state for visual changes when scrolled down
      setIsScrolled(currentScrollY > 50);
      
      // Show header at the top of page or when scrolling up
      if (currentScrollY <= 50 || !scrolledDown) {
        setIsVisible(true);
      } 
      // Hide header when scrolling down past the threshold - now on both mobile and desktop
      else if (scrolledDown && currentScrollY > 100) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    // Handle window resize to detect mobile
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [lastScrollY]);
  
  // Handle logout
  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    navigate('/login');
  };
  
  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userMenu = document.querySelector('.user-menu');
      if (userMenu && !userMenu.contains(event.target) && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);
  
  // Scroll to section function
  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    
    // If we're on the home page
    if (window.location.pathname === '/') {
      // Special case for scrolling to top
      if (sectionId === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      // Special case for the About section which has ID 'about-us'
      if (sectionId === 'about') {
        sectionId = 'about-us';
      }
      
      // Special case for the Schedule section which is inside the schedule-section div
      if (sectionId === 'schedule') {
        const scheduleSection = document.querySelector('.schedule-section');
        if (scheduleSection) {
          scheduleSection.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to home then scroll
      navigate('/');
      // Wait for navigation to complete
      setTimeout(() => {
        if (sectionId === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        
        // Special case for the About section which has ID 'about-us'
        if (sectionId === 'about') {
          sectionId = 'about-us';
        }
        
        // Special case for the Schedule section which is inside the schedule-section div
        if (sectionId === 'schedule') {
          const scheduleSection = document.querySelector('.schedule-section');
          if (scheduleSection) {
            scheduleSection.scrollIntoView({ behavior: 'smooth' });
            return;
          }
        }
        
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };
  
  return (
    <header className={`site-header ${isScrolled ? 'scrolled' : ''} ${isVisible ? 'visible' : 'hidden'}`} lang={currentLanguage}>
      <div className="container">
        <div className="header-container">
          <div className="logo-container">
            <div className="logo">
              <Link to="/" lang={currentLanguage} title={isSlovak ? "TiDeaSolis Joga Štúdio - Domov" : "TiDeaSolis Yoga Studio - Home"}>
                <img 
                  src="/images/backgrounds/header2.jpg" 
                  alt="TiDeaSolis Yoga" 
                  className="logo-image" 
                  itemProp="logo"
                />
              </Link>
            </div>
            <div className="header-controls">
              <ThemeSwitcher />
              <div className="desktop-language">
                <LanguageSwitcher />
              </div>
              {/* Mobile language switcher - visible outside hamburger menu */}
              <div className="mobile-header-language">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
          
          <button 
            className="menu-toggle" 
            onClick={toggleMenu} 
            aria-label={isSlovak ? "Prepnúť menu" : "Toggle menu"}
            aria-expanded={isMenuOpen}
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
          
          <div className={`main-nav ${isMenuOpen ? 'open' : ''}`} role="navigation" aria-label={isSlovak ? "Hlavná navigácia" : "Main navigation"}>
            <ul className="nav-links">
              <li><a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('top'); }} lang={currentLanguage}>{t('home')}</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('schedule'); }} lang={currentLanguage}>{t('schedule')}</a></li>
              {(KIDS_SPACE_PUBLIC || (currentUser && KIDS_SPACE_PREVIEW_EMAILS.includes(currentUser.email))) && (
                <li>
                  <Link to="/kids-space" onClick={() => setIsMenuOpen(false)} lang={currentLanguage} className="nav-kids-link">
                    TiDea Solis Kids
                  </Link>
                </li>
              )}
              <li><Link to="/classes" onClick={() => setIsMenuOpen(false)} lang={currentLanguage}>{t('classes')}</Link></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('instructors'); }} lang={currentLanguage}>{t('instructors')}</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('workshops'); }} lang={currentLanguage}>{t('workshops')}</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} lang={currentLanguage}>{t('about')}</a></li>
            </ul>
            
            <div className="nav-actions">
              {currentUser ? (
                <div className="user-menu">
                  <div className="username" onClick={toggleDropdown}>
                    <div className="user-avatar">
                      {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <span>{currentUser.name || currentUser.email}</span>
                  </div>
                  <div className={`user-dropdown ${isDropdownOpen ? 'open' : ''}`}>
                    <Link to="/profile" className="dropdown-item" onClick={() => {setIsMenuOpen(false); setIsDropdownOpen(false);}} lang={currentLanguage}>
                      <i className="dropdown-icon profile-icon"></i>
                      {t('my_profile')}
                    </Link>
                    {currentUser.is_admin && (
                      <Link to="/admin" className="dropdown-item" onClick={() => {setIsMenuOpen(false); setIsDropdownOpen(false);}} lang={currentLanguage}>
                        <i className="dropdown-icon admin-icon"></i>
                        {t('admin_dashboard')}
                      </Link>
                    )}
                    <button onClick={handleLogout} className="dropdown-item logout-btn" lang={currentLanguage}>
                      <i className="dropdown-icon logout-icon"></i>
                      {t('logout')}
                    </button>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="login-btn" onClick={() => setIsMenuOpen(false)} lang={currentLanguage}>
                  {t('login')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 